import { Grid, IconButton, Typography } from '@mui/material'
import { TextInputV2 } from '@labrav/react-components'
import { useStyles } from './OfferRecipientV2.styles'
import React, { useCallback, useEffect, useState } from 'react'
import { FormikProps, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import { getRandom } from '../../utils/helperFunctions'
import { OfferRecipients } from '../../../types/flyout'
import clsx from 'clsx'

export const OfferRecipientV2 = React.memo(
  ({
    formik,
    smallSize = false,
    borderLess = false,
    isReadOnly = false,
    keyName = 'offerRecipients',
  }: {
    formik: FormikProps<Record<string, unknown>>
    smallSize?: boolean
    borderLess?: boolean
    isReadOnly?: boolean
    keyName?: string
  }) => {
    const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
      formik
    const { classes } = useStyles({ borderLess })
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    const offerRecipients = (values[keyName] || []) as OfferRecipients[]

    const offerRecipientsLength = offerRecipients?.length

    useEffect(() => {
      setRandomKey(getRandom())
    }, [offerRecipients.length])

    const formChangeHandler = useCallback(
      (keyName: string) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setFieldValue(keyName, event.target.value)
        },
      [setFieldValue]
    )

    return (
      <>
        <div
          className={classes.recipientsRowsContainer}
          data-testid="update-recipients"
        >
          <FieldArray name={keyName}>
            {({ push, remove }) => (
              <>
                {offerRecipients.map((recipient, index) => (
                  <div
                    className={clsx(
                      classes.updateRow,
                      smallSize ? classes.borderRecipient : '',
                      index === offerRecipientsLength - 1 && borderLess
                        ? classes.lastRecipient
                        : '',
                      index === 0 && borderLess ? classes.firstRecipient : ''
                    )}
                    data-testid={'recipient-row'}
                  >
                    {smallSize ? (
                      <div className={classes.recipientTitleWrapper}>
                        <Typography className={classes.subTitle}>
                          Recipient {index + 1}
                        </Typography>
                        <div>
                          <RemoveRecipient
                            index={index}
                            recipientCount={offerRecipients.length}
                            remove={remove}
                            smallSize={true}
                          />
                        </div>
                      </div>
                    ) : (
                      <></>
                    )}
                    <Grid
                      container
                      spacing={3}
                      width={'100%'}
                      className={clsx(!smallSize ? classes.recipientWidth : '')}
                    >
                      <Grid
                        key={`recipient-${randomKey}-firstName`}
                        size={{
                          md: smallSize ? 6 : 3,
                        }}
                      >
                        <TextInputV2
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.firstName`}
                          onChange={formChangeHandler(
                            `${keyName}.${index}.firstName`
                          )}
                          type="string"
                          title="First name"
                          value={recipient.firstName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.firstName`,
                                false
                              ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.firstName`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.firstName`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.firstName`,
                                ' '
                              )) ||
                            ' '
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-lastName`}
                        size={{
                          md: smallSize ? 6 : 3,
                        }}
                      >
                        <TextInputV2
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.lastName`}
                          onChange={formChangeHandler(
                            `${keyName}.${index}.lastName`
                          )}
                          type="string"
                          title="Last name"
                          value={recipient.lastName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.lastName`,
                                false
                              ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.lastName`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.lastName`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.lastName`,
                                ' '
                              )) ||
                            ' '
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-title`}
                        size={{
                          md: smallSize ? 6 : 3,
                        }}
                      >
                        <TextInputV2
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.title`}
                          onChange={formChangeHandler(
                            `${keyName}.${index}.title`
                          )}
                          type="string"
                          title="Title (Optional)"
                          value={recipient.title || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.title`,
                                false
                              ) &&
                              get(errors, `${keyName}.${[index]}.title`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.title`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.title`,
                                ' '
                              )) ||
                            ' '
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-email`}
                        size={{
                          md: smallSize ? 6 : 3,
                        }}
                      >
                        <TextInputV2
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.email`}
                          onChange={handleChange}
                          type="string"
                          title="Email"
                          value={recipient.email || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.email`,
                                false
                              ) &&
                              get(errors, `${keyName}.${[index]}.email`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.email`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.email`,
                                ' '
                              )) ||
                            ' '
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    {!smallSize ? (
                      <Grid container className={classes.deleteWrapper}>
                        <RemoveRecipient
                          index={index}
                          recipientCount={offerRecipients.length}
                          remove={remove}
                        />
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </div>
                ))}

                <div
                  className={classes.addRecipientBtn}
                  data-testid={'add-a-recipient'}
                  onClick={() =>
                    push({
                      email: '',
                      firstName: '',
                      lastName: '',
                      title: '',
                    })
                  }
                >
                  Add more offer recipients
                </div>
              </>
            )}
          </FieldArray>
        </div>
      </>
    )
  }
)

const RemoveRecipient = ({
  recipientCount,
  index,
  remove,
  smallSize = false,
}: {
  recipientCount: number
  index: number
  remove: <X = any>(index: number) => X | undefined
  smallSize?: boolean
}) => {
  const { classes } = useStyles({})
  return recipientCount > 1 ? (
    <IconButton
      className={clsx(
        !smallSize ? classes.closeIconButton : classes.removeIconPadding
      )}
      onClick={() => remove(index)}
      data-testid="cancel-button"
      size="large"
    >
      <FontAwesomeIcon icon={faTrash} width={'16px'} height={'18px'} />
    </IconButton>
  ) : (
    <></>
  )
}
